import { Card, Table } from "react-bootstrap";
import ExplainLink from "../ExplainLink";
import type { AccountRecord, RequestData } from "../lib/explainTypes";
import {
  useAccountResolver,
  useHouseholdResolver,
} from "../hooks/useAccountResolver";
import { formatCurrency } from "../../../lib/numbers";

function formatAsOfDate(raw: string): string {
  // Inputs come as ISO-ish strings ("2026-05-10T00:00:00"); render MM-DD-YYYY.
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

interface Props {
  request: RequestData;
  accounts: AccountRecord[];
}

// Reads `response.request` + `response.accounts`. Per architecture spec §8.
// Name/number/balance/model-name are PII-or-pipeline-gap fields the v3
// payload doesn't carry -- they're joined back in by useAccountResolver
// from data the parent route already has loaded.
export default function ProposalRequestPanel({ request, accounts }: Props) {
  const resolve = useAccountResolver();
  const household = useHouseholdResolver();

  return (
    <Card className="mb-3">
      <Card.Header>Proposal Request</Card.Header>
      <Card.Body>
        <div>
          <strong>As of:</strong> {formatAsOfDate(request.as_of_date)}
        </div>
        <div>
          <strong>Type:</strong> {request.rebalance_type}
        </div>
        <div>
          <strong>Household:</strong>{" "}
          <ExplainLink
            to={`/clients/households/${request.household_id}/overview`}
          >
            {household.name ?? `Household ${request.household_id}`}
          </ExplainLink>
        </div>

        {accounts.length > 0 && (
          <Table size="sm" className="mt-3">
            <thead>
              <tr>
                <th>Account</th>
                <th>Model</th>
                <th className="text-end">Balance</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const r = resolve(a);
                return (
                  <tr key={a.account_id}>
                    <td>
                      {r.name}
                      {r.number && (
                        <span className="text-muted ms-1">({r.number})</span>
                      )}
                    </td>
                    <td>
                      {r.modelName && r.modelId != null ? (
                        <ExplainLink to={`/models/models/${r.modelId}`}>
                          {r.modelName}
                        </ExplainLink>
                      ) : r.modelSource === "residual" ||
                        r.modelSource === "none" ? (
                        <span className="text-muted">No Model Assigned</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="text-end">
                      {r.balance != null ? formatCurrency(r.balance) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
